import * as BlocklyCore from 'blockly/core';

import {commonI18n} from '@cdo/apps/types/locale';

/**
 * Constructs the blocks required by the flyout for the variables category.
 * @param {WorkspaceSvg} workspace The workspace containing procedures.
 * @returns {FlyoutDefinition} An array of JSON block elements.
 */
export function flyoutCategory(workspace: BlocklyCore.WorkspaceSvg) {
  const blockList: BlocklyCore.utils.toolbox.FlyoutItemInfoArray = [];
  const newVariableButton = getNewVariableButtonWithCallback(workspace);
  blockList.push(newVariableButton);

  const categoryBlocks = flyoutCategoryBlocks(workspace);
  blockList.push(...categoryBlocks);

  // Add blocks from the level toolbox XML, if present.
  blockList.push(...Blockly.cdoUtils.getCategoryBlocksJson('VARIABLE'));

  // The toolox may include "change [var] by" blocks with custom default values.
  // If any of these blocks are found, we can remove the auto-generated block.
  // Count the 'math_change' blocks in blockList.
  const mathChangeBlocksCount = blockList.filter(
    block =>
      (block as BlocklyCore.utils.toolbox.BlockInfo).type === 'math_change'
  ).length;
  // If there is more than one, remove the first occurrence which was auto-generated.
  if (mathChangeBlocksCount > 1) {
    const firstMathChangeIndex = blockList.findIndex(
      block =>
        (block as BlocklyCore.utils.toolbox.BlockInfo).type === 'math_change'
    );
    if (firstMathChangeIndex !== -1) {
      blockList.splice(firstMathChangeIndex, 1);
    }
  }

  return blockList;
}

const getNewVariableButtonWithCallback = (
  workspace: BlocklyCore.WorkspaceSvg
) => {
  const callbackkey = 'newVariableCallback';
  workspace.registerButtonCallback(callbackkey, () => {
    Blockly.FieldVariable.variableNamePrompt({
      promptText: commonI18n.renameThisPromptTitle(),
      confirmButtonLabel: commonI18n.create(),
      defaultText: '',
      callback: newName => {
        workspace.createVariable(newName);
      },
    });
  });

  return {
    kind: 'button',
    text: commonI18n.createBlocklyVariable(),
    callbackkey,
  };
};

/**
 * Construct the blocks required by the flyout for the variable category.
 *
 * @param workspace The workspace containing variables.
 * @returns {Array<Object>} An array of JSON block objects for a flyout.
 */
export function flyoutCategoryBlocks(workspace: BlocklyCore.WorkspaceSvg) {
  const variableModelList = workspace.getVariablesOfType('');

  const blockList = [];
  if (variableModelList.length > 0) {
    if (Blockly.Blocks['variables_get']) {
      variableModelList.sort(Blockly.Variables.compareByName);
      variableModelList.forEach(variable => {
        const block = {
          kind: 'block',
          type: 'variables_get',
          fields: {
            VAR: {
              name: variable.getName(),
              type: variable.getType(),
            },
          },
        };
        blockList.push(block);
      });
    }
    // New variables are added to the end of the variableModelList.
    const mostRecentVariable = variableModelList[variableModelList.length - 1];
    if (Blockly.Blocks['variables_set']) {
      const block = {
        kind: 'block',
        type: 'variables_set',
        fields: {
          VAR: {
            name: mostRecentVariable.getName(),
            type: mostRecentVariable.getType(),
          },
        },
      };
      blockList.push(block);
    }
    if (Blockly.Blocks['math_change']) {
      const block = {
        kind: 'block',
        type: 'math_change',
        fields: {
          VAR: mostRecentVariable,
        },
        inputs: {
          DELTA: {
            shadow: {
              type: 'math_number',
              fields: {
                NUM: 1,
              },
            },
          },
        },
      };
      blockList.push(block);
    }
  }
  return blockList;
}
